import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api, { setUnauthorizedHandler } from '../services/api';

const AuthContext = createContext(null);
const TOKEN_KEY = 'nnn_auth_token';

function decodeTokenPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const sessionMinutesRef = useRef(60);

  // Track user activity to detect idle sessions
  useEffect(() => {
    const onActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ['click', 'keypress', 'mousemove', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach(e => document.removeEventListener(e, onActivity));
  }, []);

  const logout = useCallback((expired = false) => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    if (expired) setSessionExpired(true);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, employee, expires_in } = res.data;
    localStorage.setItem(TOKEN_KEY, token);
    sessionMinutesRef.current = Math.ceil((expires_in || 3600) / 60);
    setSessionExpired(false);
    setUser(employee);
    return employee;
  }, []);

  // On mount: validate any stored token
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    const payload = decodeTokenPayload(token);
    if (!payload || payload.exp * 1000 < Date.now()) {
      localStorage.removeItem(TOKEN_KEY);
      setLoading(false);
      return;
    }
    api.get('/auth/me')
      .then(res => setUser(res.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  // Wire 401 responses from any API call to trigger logout
  useEffect(() => {
    setUnauthorizedHandler(() => logout(true));
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  // Load session length setting once authenticated
  useEffect(() => {
    if (!user) return;
    api.get('/auth/session-settings')
      .then(res => { sessionMinutesRef.current = res.data.session_length_minutes || 60; })
      .catch(() => {});
  }, [user]);

  // Session monitor: every 30s check for idle timeout and proactive refresh
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { logout(true); return; }

      const idleMs = Date.now() - lastActivityRef.current;
      const sessionMs = sessionMinutesRef.current * 60 * 1000;

      if (idleMs >= sessionMs) {
        logout(true);
        return;
      }

      const payload = decodeTokenPayload(token);
      if (!payload) { logout(true); return; }

      const expiresInMs = payload.exp * 1000 - Date.now();
      if (expiresInMs < 0) { logout(true); return; }

      // Refresh if token expires within 5 minutes and user was active within 5 minutes
      if (expiresInMs < 5 * 60 * 1000 && idleMs < 5 * 60 * 1000) {
        try {
          const res = await api.post('/auth/refresh');
          const { token: newToken, expires_in } = res.data;
          localStorage.setItem(TOKEN_KEY, newToken);
          sessionMinutesRef.current = Math.ceil((expires_in || 3600) / 60);
        } catch {
          // Network hiccup — don't logout, will retry next interval
        }
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, loading, sessionExpired, login, logout, sessionMinutesRef }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
